import { useMemo, useState } from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { JsonEditor } from "@/components/ui/json-editor"
import { Badge } from "@/components/ui/badge"
import { EditHistoryPane } from "@/components/domain/edit-history-pane"
import type { SharedProps } from "@/types/inertia"
import type { SkillEditable, SkillVersionRow, ToolCatalogEntry } from "@/types/skill"

type Props = {
  skill: SkillEditable
  toolCatalog: ToolCatalogEntry[]
  versions: SkillVersionRow[]
}

const USER_STATES = [
  { value: "",       label: "Any (default)" },
  { value: "active", label: "Active only" },
  { value: "banned", label: "Banned only" },
]

export default function SkillsEdit({ skill, toolCatalog, versions }: Props) {
  const { props } = usePage<SharedProps>()
  const flash = props.flash
  const isLive    = skill.live
  const isDraft   = skill.state === "draft"
  const isGlobal  = skill.isGlobal
  // Only drafts are mutable. Live and archived skills are read-only —
  // the operator must Create Draft to make changes. Globals are
  // read-only at the company layer regardless of state.
  const writable  = !isGlobal && isDraft

  const [name,        setName]        = useState(skill.name)
  const [slug,        setSlug]        = useState(skill.key)
  const [description, setDescription] = useState(skill.description ?? "")
  const [notes,       setNotes]       = useState(skill.notes ?? "")
  const [promptMd,    setPromptMd]    = useState(skill.promptMd ?? "")
  const [active,      setActive]      = useState(skill.active)
  const [position,    setPosition]    = useState(skill.position)
  const [bannedReadable, setBannedReadable] = useState(skill.bannedUserReadable)
  const [requiresUserState, setRequiresUserState] = useState(skill.requiresUserState ?? "")
  const [toolKeys,    setToolKeys]    = useState<string[]>(skill.toolKeys)
  const [toolConfigsJson, setToolConfigsJson] = useState(
    JSON.stringify(skill.toolConfigs ?? {}, null, 2),
  )
  const [dataCollectionJson, setDataCollectionJson] = useState(
    JSON.stringify(skill.dataCollection ?? [], null, 2),
  )
  const [submitting, setSubmitting] = useState(false)

  const toolsByKey = useMemo(
    () => Object.fromEntries(toolCatalog.map((t) => [t.key, t])),
    [toolCatalog],
  )

  function toggleTool(key: string) {
    setToolKeys((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]))
  }

  function save(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (submitting || !writable) return
    setSubmitting(true)
    const payload = {
      skill: {
        name,
        key: slug,
        description,
        notes,
        prompt_md: promptMd,
        active,
        position,
        banned_user_readable: bannedReadable,
        requires_user_state: requiresUserState,
        tool_keys: toolKeys,
        tool_configs_json: toolConfigsJson,
        data_collection_json: dataCollectionJson,
      },
    }
    router.patch(`/skills/${skill.id}`, payload, {
      preserveScroll: true,
      onFinish: () => setSubmitting(false),
    })
  }

  function createDraft() {
    if (!confirm(`Clone v${skill.version} into a new draft?`)) return
    router.post(`/skills/${skill.id}/draft_version`, {}, { preserveScroll: false })
  }

  function promote() {
    if (!confirm(`Promote v${skill.version} to live? Production traffic switches over immediately.`)) return
    router.patch(`/skills/${skill.id}/promote`, {}, { preserveScroll: false })
  }

  function discard() {
    if (!confirm("Discard this draft? Production is not affected.")) return
    router.delete(`/skills/${skill.id}`, { preserveScroll: false })
  }

  return (
    <AppShell>
      <PageHeader
        eyebrow={<Link href="/skills" className="hover:text-app-text">Skills</Link>}
        title={`Edit ${skill.name}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span>
              key <code className="rounded bg-white/5 px-1 text-xs">{skill.key}</code>
              {" · "}
              {skill.agentAssignments.length === 0
                ? "no agents attached"
                : `attached to ${skill.agentAssignments.length} agent${skill.agentAssignments.length === 1 ? "" : "s"}`}
            </span>
            <Badge tone={isLive ? "gold" : isDraft ? "red" : "neutral"}>
              v{skill.version} {skill.state}
            </Badge>
            {isGlobal && <Badge tone="neutral">Global</Badge>}
          </span>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Link href={`/skills/${skill.id}/test?tab=design`}>
              <Button type="button" variant="gold" size="sm">
                Open Designer →
              </Button>
            </Link>
            {isGlobal && (
              <Button
                type="button"
                variant="red"
                size="sm"
                onClick={() => {
                  if (!confirm("Clone this global skill into your company? You'll get a draft you can edit.")) return
                  router.post(`/skills/${skill.id}/clone`, {}, { preserveScroll: false })
                }}
              >
                Clone to my company
              </Button>
            )}
            {!isGlobal && isDraft && (
              <Button type="button" variant="gold" size="sm" onClick={promote}>
                Promote to live
              </Button>
            )}
            {!isGlobal && isDraft && (
              <Button type="button" variant="ghost" size="sm" onClick={discard}>
                Discard draft
              </Button>
            )}
            {!isGlobal && !isDraft && (
              <Button type="button" variant="red" size="sm" onClick={createDraft}>
                Create draft v{skill.version + 1}
              </Button>
            )}
          </div>
        }
      />

      {isGlobal && (
        <div className="rounded-control border border-border bg-white/5 px-4 py-2 text-sm text-app-muted">
          This is a global / platform-owned skill. It's read-only — clone it into your
          company to customize.
        </div>
      )}

      {!isGlobal && !isDraft && (
        <div className="rounded-control border border-vip-gold/30 bg-vip-goldMute px-4 py-2 text-sm text-vip-goldSoft">
          v{skill.version} is {skill.state} — read-only. Use
          <strong className="mx-1 text-app-text">Create draft v{skill.version + 1}</strong>
          to make changes.
        </div>
      )}

      {flash.notice && (
        <div className="rounded-control border border-status-success/30 bg-status-success/10 px-4 py-2 text-sm text-status-success">
          {flash.notice}
        </div>
      )}
      {flash.alert && (
        <div className="rounded-control border border-status-error/30 bg-status-error/10 px-4 py-2 text-sm text-status-error">
          {flash.alert}
        </div>
      )}

      <form onSubmit={save} className="space-y-5">
        <fieldset disabled={!writable} className="space-y-5 disabled:opacity-60">
        <Card className="space-y-5 p-6">
          <header>
            <h2 className="text-lg font-semibold">Basics</h2>
            <p className="text-sm text-app-muted">Identity and notes.</p>
          </header>

          <div className="grid gap-5 md:grid-cols-2">
            <Field label="Name" hint="Shown in the skills catalog and selected-agent panel.">
              <Input value={name} onChange={(e) => setName(e.target.value)} required />
            </Field>

            <Field
              label="Key"
              hint={isDraft
                ? "snake_case slug — editable until first promote."
                : "Read-only — this is where this skill plugs into the prompt and analytics."}
            >
              {isDraft ? (
                <Input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
                  required
                />
              ) : (
                <code className="block rounded-control border border-border bg-white/5 px-3 py-2 font-mono text-sm text-app-muted">
                  {skill.key}
                </code>
              )}
            </Field>

            <Field label="Position" hint="Sort order in the agent's skill list (lower = earlier).">
              <Input
                type="number"
                value={position}
                onChange={(e) => setPosition(parseInt(e.target.value, 10) || 0)}
                min={0}
              />
            </Field>

            <Field label="Active" hint="When off, the AI can't see this skill at all.">
              <label className="flex h-10 items-center gap-2 rounded-control border border-border bg-white/5 px-3 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-brand-red"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
                <span>{active ? "Active" : "Disabled"}</span>
              </label>
            </Field>
          </div>

          <Field label="Description" hint="Short blurb. Surfaces on the catalog row and tooltip.">
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              required
            />
          </Field>

          <Field
            label="Release notes"
            hint="One-line summary shown in the Versions table (e.g. 'Added banned-user guard')."
          >
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What changed in this version?"
            />
          </Field>
        </Card>

        {versions.length > 1 && (
          <Card className="space-y-3 p-6">
            <header>
              <h2 className="text-lg font-semibold">Versions</h2>
              <p className="text-sm text-app-muted">{versions.length} versions in this lineage. Promote any version to roll forward or back.</p>
            </header>
            <div className="overflow-hidden rounded-control border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-white/5 text-xs uppercase tracking-wide text-app-subtle">
                    <th className="px-3 py-2 text-left">Version</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Updated</th>
                    <th className="px-3 py-2 text-left">Notes</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {versions.map((v) => {
                    // Post-SkillVersion-refactor: row id is a SkillVersion
                    // id, the working/edit row is whichever version the
                    // presenter resolved to (skill.versionId). Edit/Promote
                    // are always per parent Skill id — cross-version edit
                    // links no longer exist; promote acts on the parent.
                    const here = v.id === skill.versionId
                    return (
                      <tr key={v.id} className={here ? "bg-brand-redSoft/30" : ""}>
                        <td className="px-3 py-2 font-medium">v{v.version}</td>
                        <td className="px-3 py-2">
                          <Badge tone={v.status === "live" ? "gold" : v.status === "draft" ? "red" : "neutral"}>
                            {v.status}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-app-muted">
                          {new Date(v.updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                        </td>
                        <td className="px-3 py-2 text-app-muted">{v.notes ?? "—"}</td>
                        <td className="px-3 py-2 text-right">
                          {!here && v.status === "draft" && (
                            <button
                              type="button"
                              onClick={() => {
                                if (!confirm(`Promote v${v.version} to live?`)) return
                                router.patch(`/skills/${skill.id}/promote`, {}, { preserveScroll: false })
                              }}
                              className="text-xs text-brand-red hover:underline"
                            >
                              Promote
                            </button>
                          )}
                          {here && <span className="text-xs text-app-subtle">current</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        <Card className="space-y-3 p-6">
          <header>
            <h2 className="text-lg font-semibold">Prompt</h2>
            <p className="text-sm text-app-muted">
              Markdown injected into the agent's system prompt under{" "}
              <code className="rounded bg-white/5 px-1 py-0.5 text-xs">## Skill: {skill.name}</code>{" "}
              whenever this skill is active and the appuser state passes the audience gate.
            </p>
          </header>

          <details className="rounded-control border border-border bg-white/5 px-4 py-3 text-xs text-app-muted">
            <summary className="cursor-pointer text-sm font-medium text-app-text">
              Conventions used by existing skills (click to expand)
            </summary>
            <div className="mt-3 space-y-3">
              <div>
                <div className="font-semibold text-app-text">Two required sections</div>
                <ul className="ml-4 mt-1 list-disc space-y-1">
                  <li>
                    <code className="font-mono">Trigger:</code> — one or two sentences describing
                    when this skill should activate (what the user just said, the state of the
                    convo, what other skill chained here).
                  </li>
                  <li>
                    <code className="font-mono">Process:</code> — step-by-step what to do. Name
                    each tool call with its real key + example params:{" "}
                    <code className="font-mono">fetch_partner_data(endpoint_name: "coin_balance")</code>.
                  </li>
                </ul>
              </div>

              <div>
                <div className="font-semibold text-app-text">Optional sections (use as needed)</div>
                <ul className="ml-4 mt-1 list-disc space-y-1">
                  <li>
                    <code className="font-mono">Decide first:</code> — branch at the top of a
                    multi-flow skill (e.g. visual vs non-visual bug). Name each flow:{" "}
                    <em>Visual bug flow (3 turns):</em>, then numbered steps.
                  </li>
                  <li>
                    <code className="font-mono">Rules:</code> /{" "}
                    <code className="font-mono">Critical rules:</code> — hard constraints. Use
                    DO / DO NOT bullets. The LLM follows these strongly when they're framed as
                    rules.
                  </li>
                  <li>
                    <code className="font-mono">On error:</code> /{" "}
                    <code className="font-mono">On not-found:</code> — explicit fallback paths
                    so the LLM doesn't ad-lib recovery.
                  </li>
                  <li>
                    <code className="font-mono">Examples:</code> — input → expected handling
                    pairs. Useful for keyword extraction, classification, or dedup logic.
                  </li>
                  <li>
                    Reference / guide blocks (e.g.{" "}
                    <code className="font-mono">Keyword extraction guide:</code>) — auxiliary
                    rules the LLM consults mid-flow.
                  </li>
                </ul>
              </div>

              <div>
                <div className="font-semibold text-app-text">What the agent already knows (don't repeat)</div>
                <p className="mt-1">
                  PromptComposer auto-prepends the agent's identity + voice/tone, and auto-appends:
                </p>
                <ul className="ml-4 mt-1 list-disc space-y-1">
                  <li><code className="font-mono">## Tools</code> — one-line description per tool key in the union.</li>
                  <li><code className="font-mono">## FAQ Index</code> — if any active skill sets <code className="font-mono">tool_configs.lookup_faq.inline_index_in_prompt: true</code>.</li>
                  <li><code className="font-mono">## Closure</code> — global decision tree on when to call <code className="font-mono">request_rating</code> / <code className="font-mono">escalate</code>.</li>
                  <li>Per-turn: convo state, turn count, attachments list, file-cap status, collected slots, banned-mode override.</li>
                </ul>
                <p className="mt-1">
                  So skip generic guidance about closure, escalation, or rating — describe only
                  what's specific to <em>this</em> skill.
                </p>
              </div>

              <div>
                <div className="font-semibold text-app-text">Hard rules to keep close</div>
                <ul className="ml-4 mt-1 list-disc space-y-1">
                  <li>
                    "Same turn" matters — when two tool calls must fire together (e.g.{" "}
                    <code className="font-mono">request_rating</code> +{" "}
                    <code className="font-mono">submit_feedback</code>), say so explicitly:
                    "in the SAME assistant turn emit BOTH tool calls BEFORE any prose."
                  </li>
                  <li>
                    "Do NOT call X more than once per convo" is the convention for terminal /
                    side-effect tools (escalate, submit_feedback). Spell it out.
                  </li>
                  <li>
                    Use <code className="font-mono">&lt;placeholder&gt;</code> syntax for
                    LLM-filled values (<code className="font-mono">reason: &lt;user's words&gt;</code>),
                    NOT <code className="font-mono">{`{{mustache}}`}</code> — there is no template engine.
                  </li>
                  <li>
                    Capitalize and bold critical negatives (DO NOT, NEVER, ONLY) — the LLM
                    weights these heavier than lowercase prose.
                  </li>
                </ul>
              </div>
            </div>
          </details>

          <ConfiguredEndpointsBanner toolConfigsJson={toolConfigsJson} />
          <Textarea
            value={promptMd}
            onChange={(e) => setPromptMd(e.target.value)}
            rows={16}
            className="font-mono text-xs"
            placeholder={`Trigger: <one sentence — when this skill activates>.\nProcess:\n1. <step>\n2. call some_tool(param: <value>)\n3. <step>\n\nRules:\n- DO call request_rating in the same turn as the side-effect tool.\n- DO NOT call this skill's terminal tool more than once per convo.`}
          />
        </Card>

        <Card className="space-y-4 p-6">
          <header>
            <h2 className="text-lg font-semibold">Tools</h2>
            <p className="text-sm text-app-muted">
              The set of tools the AI can use from this skill. Always-on safety
              tools (detect_abuse, flag_rage) are present on every agent regardless.
            </p>
          </header>

          <div className="grid gap-2 md:grid-cols-2">
            {toolCatalog.map((t) => {
              const checked = toolKeys.includes(t.key)
              return (
                <label
                  key={t.key}
                  className={[
                    "flex cursor-pointer items-start gap-3 rounded-control border px-3 py-2 text-sm transition",
                    checked
                      ? "border-brand-red/40 bg-brand-redSoft"
                      : "border-border bg-white/5 hover:bg-white/10",
                  ].join(" ")}
                >
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 accent-brand-red"
                    checked={checked}
                    onChange={() => toggleTool(t.key)}
                    disabled={t.alwaysOn}
                  />
                  <span className="min-w-0">
                    <span className="flex items-center gap-2">
                      <code className="font-mono text-xs text-app-text">{t.key}</code>
                      {t.alwaysOn && <Badge tone="gold">always on</Badge>}
                    </span>
                    <span className="mt-1 block text-xs text-app-muted">{t.description}</span>
                  </span>
                </label>
              )
            })}
          </div>

          <Field
            label="Tool configs (JSON)"
            hint={
              toolKeys.length > 0
                ? `Settings for each tool the agent can use here — keys: ${toolKeys.join(", ")}.`
                : "Settings for each tool the agent can use here. Shape is { tool_key: { … } }."
            }
          >
            <JsonEditor
              value={toolConfigsJson}
              onChange={setToolConfigsJson}
              minHeight="220px"
              placeholder={`{\n  "fetch_partner_data": { "allowed_endpoints": ["coin_balance"] }\n}`}
            />
          </Field>

          {toolKeys.length > 0 && (
            <Card variant="inset" className="p-4 text-xs text-app-muted">
              <div className="mb-2 font-medium text-app-text">Selected tool schemas (read-only)</div>
              <div className="space-y-3">
                {toolKeys.map((k) => {
                  const t = toolsByKey[k]
                  if (!t) return null
                  return (
                    <details key={k}>
                      <summary className="cursor-pointer text-app-text">
                        <code className="font-mono">{k}</code>
                      </summary>
                      <pre className="mt-2 max-h-48 overflow-auto rounded-control bg-app-bg p-3 text-[11px] leading-relaxed">
{JSON.stringify(t.schema, null, 2)}
                      </pre>
                    </details>
                  )
                })}
              </div>
            </Card>
          )}
        </Card>

        <Card className="space-y-4 p-6">
          <header>
            <h2 className="text-lg font-semibold">Audience</h2>
            <p className="text-sm text-app-muted">Which appuser states see this skill.</p>
          </header>

          <div className="grid gap-5 md:grid-cols-2">
            <Field label="Requires user state" hint="Restrict skill to one appuser state. Default: any.">
              <select
                value={requiresUserState}
                onChange={(e) => setRequiresUserState(e.target.value)}
                className="h-10 w-full rounded-control border border-border bg-white/5 px-3 text-sm text-app-text"
              >
                {USER_STATES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </Field>

            <Field label="Banned-user readable" hint="Allow banned users to see this skill regardless of state gate.">
              <label className="flex h-10 items-center gap-2 rounded-control border border-border bg-white/5 px-3 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-brand-red"
                  checked={bannedReadable}
                  onChange={(e) => setBannedReadable(e.target.checked)}
                />
                <span>{bannedReadable ? "Visible to banned users" : "Hidden from banned users"}</span>
              </label>
            </Field>
          </div>
        </Card>

        <Card className="space-y-3 p-6">
          <header>
            <h2 className="text-lg font-semibold">Data collection</h2>
            <p className="text-sm text-app-muted">
              Structured fields the skill is supposed to elicit during the convo.
              Array of objects, e.g. <code className="rounded bg-white/5 px-1 py-0.5 text-xs">{`[{ "field_key": "order_id", "question": "…", "required": true }]`}</code>.
            </p>
          </header>
          <JsonEditor
            value={dataCollectionJson}
            onChange={setDataCollectionJson}
            minHeight="180px"
            placeholder={`[\n  { "field_key": "order_id", "question": "What's your order id?", "required": true }\n]`}
          />
        </Card>
        </fieldset>

        <div className="flex justify-end gap-3">
          <Link href={`/skills/${skill.id}`}>
            <Button type="button" variant="ghost">Cancel</Button>
          </Link>
          {writable && (
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Save changes"}
            </Button>
          )}
        </div>
      </form>

      <Card className="space-y-3 p-6">
        <header>
          <h2 className="text-lg font-semibold">Edit History</h2>
          <p className="text-sm text-app-muted">
            Every save records who changed what. Forge co-edits, manual saves, and version flips show up here.
          </p>
        </header>
        <EditHistoryPane entries={skill.editHistory ?? []} />
      </Card>
    </AppShell>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-sm font-medium text-app-text">{label}</div>
      {hint && <div className="mb-2 text-xs text-app-muted">{hint}</div>}
      {children}
    </label>
  )
}

// Surfaces endpoints already wired through tool_configs so the operator
// doesn't re-describe the call mechanics in the Process section. The
// runtime already knows HOW to call each allowed_endpoint — the prompt
// just needs to say WHEN to call it. Reduces the prompt-vs-config drift
// David flagged.
function ConfiguredEndpointsBanner({ toolConfigsJson }: { toolConfigsJson: string }) {
  let endpointEntries: Array<{ tool: string; endpoints: string[] }> = []
  try {
    const cfg = JSON.parse(toolConfigsJson || "{}")
    if (cfg && typeof cfg === "object") {
      for (const [toolKey, val] of Object.entries(cfg as Record<string, unknown>)) {
        if (val && typeof val === "object" && "allowed_endpoints" in (val as Record<string, unknown>)) {
          const eps = (val as { allowed_endpoints?: unknown }).allowed_endpoints
          if (Array.isArray(eps) && eps.length > 0) {
            endpointEntries.push({ tool: toolKey, endpoints: eps.map(String) })
          }
        }
      }
    }
  } catch { /* json invalid — banner just hides until they fix it */ }

  if (endpointEntries.length === 0) return null

  return (
    <div className="mb-2 rounded-control border border-vip-gold/30 bg-vip-goldMute/30 px-3 py-2 text-[11px] text-vip-goldSoft">
      <div className="font-semibold text-app-text">
        {endpointEntries.reduce((n, e) => n + e.endpoints.length, 0)} endpoint
        {endpointEntries.reduce((n, e) => n + e.endpoints.length, 0) === 1 ? "" : "s"} wired via tool config
      </div>
      <div className="mt-0.5 text-app-muted">
        The runtime already knows <em>how</em> to call these — your{" "}
        <code className="font-mono text-app-text">Process:</code> section only needs to say <em>when</em>.
        Skip step-by-step "first set X header, then GET /…" prose.
      </div>
      <ul className="mt-1.5 space-y-0.5">
        {endpointEntries.map((e) => (
          <li key={e.tool} className="font-mono text-app-text">
            <span className="text-app-muted">{e.tool}:</span>{" "}
            {e.endpoints.map((ep, i) => (
              <span key={ep}>
                {i > 0 && <span className="text-app-subtle">, </span>}
                <code className="rounded bg-white/5 px-1">{ep}</code>
              </span>
            ))}
          </li>
        ))}
      </ul>
    </div>
  )
}
